import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { preventDefaultBehavior } from 'assets/utils/dom/Events';
import { useRef } from 'react';
import { ProviderForm } from '../Provider.Edit';
import { compact } from 'lodash';

export default function ProviderLogoTranslation({
  logo,
  index,
  languageIsoCode,
}: {
  logo?: Utils.Unpacked<ProviderForm['logos']>;
  index: number;
  languageIsoCode: Locale.Languages;
}) {
  const { lang } = getLocales();
  const fileInput = useRef<HTMLInputElement>();
  const { form } = useModalForm<ProviderForm>();
  const foundLogo = logo?.isDeleted ? null : logo;
  const foundTranslation = compact(foundLogo?.translatedLogos).find(
    (d) => d.languageIsoCode === languageIsoCode && !d.isDeleted
  );
  const logoExists = languageIsoCode === Language.english ? !!foundLogo : !!foundTranslation;

  const alreadyUploadedLogo =
    languageIsoCode === Language.english
      ? foundLogo?.id
        ? foundLogo?.mediaFile
        : null
      : foundTranslation?.id
        ? foundTranslation?.mediaFile
        : null;

  function handleDelete() {
    if (form.data.logos && form.data.logos[index]) {
      form.data.logos[index].isDeleted = true;
      form.data.logos[index].translatedLogos = compact(form.data.logos[index].translatedLogos).map((t) =>
        t.languageIsoCode === languageIsoCode ? { ...t, isDeleted: true } : t
      );
    }
    form.update({ logos: [...form.data.logos] });
  }
  function handleDeleteTranslation() {
    if (form.data.logos && form.data.logos[index]) {
      form.data.logos[index].translatedLogos = compact(form.data.logos[index].translatedLogos).map((t) =>
        t.languageIsoCode === languageIsoCode ? { ...t, isDeleted: true } : t
      );
    }
    form.update({ logos: [...form.data.logos] });
  }

  function handleUpload(formFile?: File) {
    if (formFile) {
      form.update({
        logos: [
          ...form.data.logos,
          {
            mediaFile: formFile,
            translatedLogos: [],
          },
        ],
      });
    }
  }

  function handleUploadTranslation(formFile?: File) {
    if (form.data.logos && form.data.logos[index]) {
      form.data.logos[index].translatedLogos = compact(form.data.logos[index].translatedLogos).map((t) =>
        t.languageIsoCode === languageIsoCode ? { ...t, isDeleted: true } : t
      );
      form.data.logos[index].translatedLogos.push({
        documentType: 'ProviderLogo',
        languageIsoCode,
        formFile,
      });
    }
    form.update({ logos: [...form.data.logos] });
  }

  return (
    <>
      {logoExists ? (
        <Button
          class={ThemeButtonCircle}
          media={<Icon class="fas-trash" />}
          style={{ display: 'inline-flex', color: Theme.Color.error }}
          onClick={() => {
            if (languageIsoCode === Language.english) {
              handleDelete();
            } else if (foundLogo) {
              handleDeleteTranslation();
            }
          }}
          htmlElementProps={{
            title: lang.delete,
          }}
        />
      ) : (
        <>
          <input
            ref={fileInput}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              preventDefaultBehavior(event);
              if (languageIsoCode === Language.english) {
                handleUpload(event.currentTarget.files[0]);
              } else if (foundLogo) {
                handleUploadTranslation(event.currentTarget.files[0]);
              }
              event.currentTarget.value = '';
            }}
            type="file"
            accept="*"
            multiple={false}
            style={{ display: 'none' }}
          />
          <Button
            class={ThemeButtonCircle}
            style={{ display: 'inline-flex', color: Theme.Color.success }}
            media={<Icon class="fas-upload" />}
            disabled={languageIsoCode == Language.english ? false : !foundLogo}
            onClick={() => fileInput?.current.click()}
            htmlElementProps={{
              title: lang.upload,
            }}
          />
        </>
      )}
      <LinkButton
        media={<Icon class="fas-download" />}
        style={{ display: 'inline-flex' }}
        class={ThemeButtonCircle}
        disabled={!alreadyUploadedLogo}
        htmlElementProps={{
          title: `${lang.download} - ${alreadyUploadedLogo?.name}`,
        }}
        url={alreadyUploadedLogo?.url}
      />
    </>
  );
}
